import React from 'react';
import styles from './PointsConfirmationModal.module.css';

interface PointsConfirmationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  featureName: string;
  pointsCost: number;
  remainingPoints: number;
  amount?: number;
  loading?: boolean;
}

const PointsConfirmationModal: React.FC<PointsConfirmationModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  featureName,
  pointsCost,
  remainingPoints,
  amount = 1,
  loading = false,
}) => {
  if (!isOpen) return null;

  const hasEnoughPoints = remainingPoints >= pointsCost;
  const remainingAfter = Math.max(0, remainingPoints - pointsCost);

  return (
    <div className={styles.overlay} onClick={onClose}>
      <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
        <div className={styles.header}>
          <h2 className={styles.title}>Confirm Action</h2>
          <button 
            className={styles.closeButton}
            onClick={onClose}
            disabled={loading}
          >
            ✕
          </button>
        </div>

        <div className={styles.content}>
          <div className={styles.feature}>
            <div className={styles.featureIcon}>💡</div>
            <div className={styles.featureDetails}>
              <h3 className={styles.featureName}>{featureName}</h3>
              {amount > 1 && (
                <p className={styles.amount}>Quantity: {amount}</p>
              )}
            </div>
          </div>

          <div className={styles.costBreakdown}>
            <div className={styles.costItem}>
              <span className={styles.label}>Points Required:</span>
              <span className={styles.value}>{pointsCost.toLocaleString()}</span>
            </div>
            
            <div className={styles.costItem}>
              <span className={styles.label}>Current Balance:</span>
              <span className={styles.value}>{remainingPoints.toLocaleString()}</span>
            </div>
            
            <div className={styles.separator}></div>
            
            <div className={styles.costItem}>
              <span className={styles.label}>Balance After:</span>
              <span className={`${styles.value} ${hasEnoughPoints ? styles.positive : styles.negative}`}>
                {hasEnoughPoints ? remainingAfter.toLocaleString() : 'Insufficient'}
              </span>
            </div>
          </div>

          {!hasEnoughPoints && (
            <div className={styles.warning}>
              <div className={styles.warningIcon}>⚠️</div>
              <div className={styles.warningText}>
                <p><strong>Insufficient Points</strong></p>
                <p>You need {(pointsCost - remainingPoints).toLocaleString()} more points to complete this action.</p>
              </div>
            </div>
          )}
        </div>

        <div className={styles.actions}>
          <button 
            className={styles.cancelButton}
            onClick={onClose}
            disabled={loading}
          >
            Cancel
          </button>
          
          {hasEnoughPoints ? (
            <button 
              className={styles.confirmButton}
              onClick={onConfirm}
              disabled={loading}
            >
              {loading ? (
                <>
                  <div className={styles.spinner}></div>
                  Processing...
                </>
              ) : (
                `Use ${pointsCost.toLocaleString()} Points`
              )}
            </button>
          ) : (
            <button 
              className={styles.upgradeButton}
              onClick={() => window.location.href = '/pricing'}
            >
              Buy More Points
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default PointsConfirmationModal;